// Spoken-math matcher: how strongly does any VALID solution appear in the transcript?
//
// We normalise the transcript to a stream of math tokens (numbers + operators), drop
// all filler, then fuzzy-search every valid solution's signature through that stream
// with approximate substring matching (token-level edit distance). The best closeness,
// with a recency boost, becomes a 0–5★ confidence. This never claims the player was
// wrong — it's a confidence that we heard a winning answer, which they then adjust.

import { solveAll } from './solver'
import { verifyChain } from './chainVerify'

// How many trailing tokens count as "the end" for the recency boost.
const RECENT_TOKENS = 12

// Confidence at/above which we "fire": we're sure enough the player spoke a winning
// answer to credit it without asking them to type. Conservative on purpose — a false
// "you got it!" is worse UX than asking for the answer, and the typed fallback is exact.
export const FIRE_SIMILARITY = 0.9

const UNITS: Record<string, number> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  // common speech-to-text homophones for digits
  to: 2, too: 2, for: 4, fore: 4, ate: 8, won: 1, tree: 3,
}
const TEENS: Record<string, number> = {
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19,
}
const TENS: Record<string, number> = {
  twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90,
}
const OPWORDS: Record<string, string> = {
  times: '*', x: '*', multiplied: '*', multiply: '*', product: '*',
  plus: '+', add: '+', added: '+',
  minus: '-', subtract: '-', subtracted: '-', less: '-',
  divided: '/', divide: '/', divides: '/', over: '/',
}
const OPSYMBOLS: Record<string, string> = { '×': '*', '÷': '/', '*': '*', '/': '/', '+': '+', '-': '-' }

// Parse a number starting at word index i; returns its value + how many words consumed.
const parseNumberAt = (words: string[], i: number): { value: number; len: number } | null => {
  const w = words[i]
  if (/^\d+$/.test(w)) return { value: parseInt(w, 10), len: 1 }
  if (TENS[w] !== undefined) {
    const next = words[i + 1]
    if (next && UNITS[next] !== undefined && UNITS[next] >= 1 && UNITS[next] <= 9) {
      return { value: TENS[w] + UNITS[next], len: 2 }
    }
    return { value: TENS[w], len: 1 }
  }
  if (TEENS[w] !== undefined) return { value: TEENS[w], len: 1 }
  if (UNITS[w] !== undefined) return { value: UNITS[w], len: 1 }
  return null
}

// Transcript → array of math token symbols (digit strings + operator chars), filler dropped.
export const tokenizeSpokenMath = (text: string): string[] => {
  const words = (text || '')
    .toLowerCase()
    .replace(/[×÷*/+]/g, ' $& ') // isolate operator symbols
    .replace(/(\d)\s*-\s*(\d)/g, '$1 - $2') // isolate minus between digits
    .split(/[^a-z0-9+\-*/×÷]+/)
    .filter(Boolean)

  const tokens: string[] = []
  let i = 0
  while (i < words.length) {
    const w = words[i]
    const num = parseNumberAt(words, i)
    if (num) {
      tokens.push(String(num.value))
      i += num.len
      continue
    }
    if (OPSYMBOLS[w]) {
      tokens.push(OPSYMBOLS[w])
      i += 1
      continue
    }
    if (OPWORDS[w]) {
      tokens.push(OPWORDS[w])
      i += 1
      continue
    }
    i += 1 // filler — skip
  }
  return tokens
}

// Best approximate match of `pattern` as a substring of `text` (free start + end).
// Returns the minimum edit distance and the text index where the best match ends.
const approxSubstringMatch = (pattern: string[], text: string[]): { dist: number; end: number } => {
  const m = pattern.length
  const n = text.length
  if (m === 0) return { dist: 0, end: 0 }
  let prev = new Array(n + 1).fill(0) // empty pattern matches anywhere at cost 0
  for (let pi = 1; pi <= m; pi++) {
    const cur = new Array(n + 1)
    cur[0] = pi
    for (let tj = 1; tj <= n; tj++) {
      const cost = pattern[pi - 1] === text[tj - 1] ? 0 : 1
      cur[tj] = Math.min(prev[tj - 1] + cost, prev[tj] + 1, cur[tj - 1] + 1)
    }
    prev = cur
  }
  let best = Infinity
  let end = 0
  for (let j = 0; j <= n; j++) {
    if (prev[j] < best) {
      best = prev[j]
      end = j
    }
  }
  return { dist: best, end }
}

export type MatchResult = {
  fired: boolean // confident enough to credit the answer from speech alone
  stars: number // 0–5 auto-confidence (kept for analysis / calibration)
  similarity: number // 0–1 best closeness to any valid solution
  recent: boolean // best match fell in the final tokens
  matchedSolution: string | null // the valid expression best/ chain matched
  method: 'fuzzy' | 'chain' | null // which signal fired (null = neither)
}

const toStars = (similarity: number, recent: boolean): number => {
  let s = similarity >= 0.85 ? 4 : similarity >= 0.65 ? 3 : similarity >= 0.45 ? 2 : similarity >= 0.25 ? 1 : 0
  if (s > 0 && recent) s = Math.min(5, s + 1)
  return s
}

export const scoreTranscript = (text: string, numbers: number[]): MatchResult => {
  const solutions = solveAll(numbers)
  const tokens = tokenizeSpokenMath(text)

  // --- Pass 1: fuzzy template match (cheap, handles recited expressions) ---
  let best = { similarity: -1, end: 0, expr: null as string | null }
  if (tokens.length > 0 && solutions.length > 0) {
    for (const sol of solutions) {
      const { dist, end } = approxSubstringMatch(sol.signature, tokens)
      const similarity = 1 - dist / sol.signature.length
      if (similarity > best.similarity) best = { similarity, end, expr: sol.expr }
    }
  }
  const recent = tokens.length - best.end <= RECENT_TOKENS
  const similarity = Math.max(0, best.similarity)
  const fuzzy: MatchResult = {
    fired: similarity >= FIRE_SIMILARITY,
    stars: toStars(best.similarity, recent),
    similarity,
    recent,
    matchedSolution: best.expr,
    method: similarity >= FIRE_SIMILARITY ? 'fuzzy' : null,
  }
  if (fuzzy.fired) return fuzzy

  // --- Pass 2 (only if fuzzy didn't fire): verify the stated working ---
  // Catches "narrated working" (intermediate results) that the template match misses.
  // A verified chain is arithmetically certain, so it auto-fires.
  const chain = verifyChain(text, numbers)
  if (chain.solved) {
    return { fired: true, stars: 5, similarity: 1, recent: true, matchedSolution: chain.expression, method: 'chain' }
  }
  return fuzzy
}
